<template>
  <div class="attachments-container">
    <div v-if="loading && !attachments.length" class="loading-state">
      {{ __('Loading…') }}
    </div>

    <div v-else class="attachments-content">
      <!-- Upload Zone -->
      <div v-if="canUpload" class="upload-tools">
        <div
          class="upload-zone"
          :class="{ 'drag-over': isDragging }"
          @click="openFilePicker"
          @dragenter.prevent="handleDragEnter"
          @dragover.prevent="handleDragOver"
          @dragleave.prevent="handleDragLeave"
          @drop.prevent="handleDrop"
        >
          <input
            ref="fileInput"
            type="file"
            multiple
            style="display: none"
            accept="image/*,video/*,audio/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/zip,application/x-rar-compressed,image/svg+xml,application/vnd.apple.keynote,application/vnd.apple.pages,application/vnd.apple.numbers,application/epub+zip"
            @change="handleFileSelect"
          />

          <div class="upload-zone-content">
            <div class="upload-text">
              <p class="upload-title">
                <UploadIcon class="upload-icon" />
                {{ s__('Attachments|Drop files here or click to upload') }}
              </p>
              <p class="upload-subtitle">
                {{
                  s__('Attachments|Supports: PDF, DOC, Images, Video, Audio, Archives (Max 100 MB)')
                }}
                <button
                  type="button"
                  class="upload-subtitle-link"
                  @click.stop="showFileTypesModal = true"
                >
                  {{ s__('Attachments|View all supported file types') }}
                </button>
              </p>
            </div>
          </div>
        </div>
        <button
          v-if="canAttachExisting"
          type="button"
          class="attach-existing-btn"
          @click="openExistingAttachmentModal"
        >
          {{ s__('Attachments|Attach existing file') }}
        </button>
      </div>

      <!-- Global Upload Errors -->
      <AlertBanner
        v-if="showErrorBanner"
        type="error"
        :title="s__('Attachments|Upload Failed')"
        :message="uploadError"
        :dismissible="true"
        :duration="0"
        @dismiss="showErrorBanner = false"
      />

      <!-- Download Errors -->
      <AlertBanner
        v-if="downloadError"
        type="error"
        :title="s__('Attachments|Download Failed')"
        :message="downloadError"
        :dismissible="true"
        :duration="0"
        @dismiss="downloadError = null"
      />

      <!-- Send to Telegram Info -->
      <AlertBanner
        v-if="sendToTelegramInfo"
        type="info"
        :title="s__('Attachments|Sending to Telegram')"
        :message="
          s__(
            'Attachments|Your attachment is being sent to your Telegram Direct Messages. This may take a few moments.'
          )
        "
        :dismissible="true"
        :duration="5000"
        @dismiss="sendToTelegramInfo = false"
      />

      <!-- Send to Telegram Errors -->
      <AlertBanner
        v-if="sendToTelegramError"
        type="error"
        :title="s__('Attachments|Send to Telegram Failed')"
        :message="sendToTelegramError"
        :dismissible="true"
        :duration="0"
        @dismiss="sendToTelegramError = null"
      />

      <!-- WebView Info Modal -->
      <AttachmentsWebViewInfoModal
        :visible="showWebViewInfoModal"
        @close="showWebViewInfoModal = false"
      />

      <!-- File Types Info Modal -->
      <AttachmentsFileTypesModal
        :visible="showFileTypesModal"
        @close="showFileTypesModal = false"
      />

      <div
        v-if="showExistingAttachmentModal"
        class="attach-existing-modal-backdrop"
        @click.self="closeExistingAttachmentModal"
      >
        <div class="attach-existing-modal" role="dialog" aria-modal="true">
          <div class="attach-existing-modal-header">
            <h3>{{ s__('Attachments|Attach existing project file') }}</h3>
            <button type="button" class="btn-icon-cancel" @click="closeExistingAttachmentModal">
              <CloseIcon />
            </button>
          </div>

          <input
            v-model.trim="existingAttachmentSearch"
            type="search"
            class="attach-existing-search"
            :placeholder="s__('Attachments|Search files')"
          />

          <div v-if="projectAttachmentsLoading" class="loading-state">
            {{ __('Loading…') }}
          </div>
          <div v-else-if="filteredExistingAttachments.length" class="attach-existing-list">
            <label
              v-for="attachment in filteredExistingAttachments"
              :key="attachment.id"
              class="attach-existing-item"
            >
              <input
                v-model="selectedExistingAttachmentIds"
                type="checkbox"
                :value="attachment.id"
              />
              <div class="file-icon-wrapper" :class="getFileIconClass(attachment.filename)">
                <component
                  :is="getFileIcon(attachment.contentType, attachment.filename)"
                  class="file-type-icon"
                />
              </div>
              <div class="attachment-details">
                <p class="attachment-filename">{{ attachment.filename }}</p>
                <p class="attachment-meta">
                  {{ attachment.humanSize }} · {{ formatDate(attachment.createdAt) }}
                </p>
              </div>
            </label>
          </div>
          <div v-else class="empty-state">
            {{ s__('Attachments|No project files available to attach') }}
          </div>

          <p v-if="attachExistingError" class="attach-existing-error">{{ attachExistingError }}</p>

          <div class="attach-existing-actions">
            <button
              type="button"
              class="btn-sm btn-secondary"
              @click="closeExistingAttachmentModal"
            >
              {{ s__('Attachments|Cancel') }}
            </button>
            <button
              type="button"
              class="btn-sm btn-primary"
              :disabled="attachingExisting || selectedExistingAttachmentIds.length === 0"
              @click="attachSelectedExistingAttachments"
            >
              {{
                attachingExisting
                  ? s__('Attachments|Attaching...')
                  : s__('Attachments|Attach files')
              }}
            </button>
          </div>
        </div>
      </div>

      <!-- Upload Progress -->
      <div v-if="uploadingFiles.length" class="upload-progress-section">
        <div v-for="file in uploadingFiles" :key="file.id" class="upload-progress-item">
          <div class="file-icon-wrapper" :class="getFileIconClass(file.name)">
            <component :is="getIconForProgress(file)" class="file-type-icon" />
          </div>
          <div class="file-progress-info">
            <div class="file-progress-header">
              <p class="file-progress-name">{{ file.name }}</p>
              <span
                v-if="
                  file.status === 'uploading' ||
                  file.status === 'processing' ||
                  (file.status === 'success' && file.progress < 100)
                "
                class="file-progress-percent"
              >
                {{ file.progress }}%
              </span>
              <span
                v-else-if="file.status === 'success' && file.progress === 100"
                class="file-progress-percent"
              >
                100%
              </span>
            </div>
            <div
              v-if="
                file.status === 'uploading' ||
                file.status === 'processing' ||
                file.status === 'success'
              "
              class="progress-bar"
            >
              <div class="progress-bar-fill" :style="{ width: `${file.progress}%` }"></div>
            </div>
            <p
              v-if="file.status === 'success' && file.progress === 100"
              class="file-progress-success"
            >
              {{ s__('Attachments|Upload completed') }}
            </p>
            <p v-else-if="file.status === 'error'" class="file-progress-error">
              {{ file.error }}
            </p>
          </div>
          <div
            v-if="file.status === 'uploading' || file.status === 'processing'"
            class="upload-actions"
          >
            <button
              type="button"
              class="btn-icon-cancel"
              @click="cancelUpload(file.id)"
              :title="s__('Attachments|Cancel upload')"
            >
              <CloseIcon />
            </button>
          </div>
          <div v-else-if="file.status === 'error'" class="upload-actions">
            <button type="button" class="btn-retry" @click="retryUpload(file.id)">
              {{ s__('Attachments|Retry') }}
            </button>
            <button type="button" class="btn-icon-dismiss" @click="dismissFailedUpload(file.id)">
              <CloseIcon />
            </button>
          </div>
        </div>
      </div>

      <!-- Storage Info (always visible, project-level) -->
      <div v-if="storageInfo" class="storage-info-section">
        <span class="storage-info-badge">
          {{
            s__('Attachments|Storage: %{used} / %{limit} (%{percent}%)')
              .replace('%{used}', `${storageInfo.usedMb} MB`)
              .replace('%{limit}', `${storageInfo.limitMb} MB`)
              .replace('%{percent}', storageInfo.percentageUsed)
          }}
        </span>
      </div>

      <!-- Attachments List -->
      <div v-if="attachments.length" class="attachments-list-section">
        <div class="attachments-header">
          <div class="attachments-header-left">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="paperclip-icon"
            >
              <path
                d="m16 6-8.414 8.586a2 2 0 0 0 2.829 2.829l8.414-8.586a4 4 0 1 0-5.657-5.657l-8.379 8.551a6 6 0 1 0 8.485 8.485l8.379-8.551"
              ></path>
            </svg>
            <span
              >{{ s__('Attachments|Attachments') }} ({{
                lastKnownCount !== null ? lastKnownCount : attachments.length
              }})</span
            >
            <button
              v-if="isTelegramWebView()"
              type="button"
              class="webview-warning-btn-header"
              :title="
                s__(
                  'Attachments|File attachments download may not properly work in WebView. Click for more information.'
                )
              "
              @click="showWebViewInfoModal = true"
              style="margin-left: 8px"
            >
              <WarningIcon :size="18" />
            </button>
          </div>
        </div>

        <div class="attachments-list">
          <div v-for="attachment in attachments" :key="attachment.id" class="attachment-item">
            <div class="attachment-main-row">
              <div class="file-icon-wrapper" :class="getFileIconClass(attachment.filename)">
                <component
                  :is="getFileIcon(attachment.contentType, attachment.filename)"
                  class="file-type-icon"
                />
              </div>
              <div class="attachment-details">
                <p class="attachment-filename">{{ attachment.filename }}</p>
                <p class="attachment-meta">
                  {{ attachment.humanSize }} · {{ formatDate(attachment.createdAt) }}
                </p>
              </div>
            </div>
            <div class="attachment-actions">
              <button
                v-if="isImageAttachment(attachment)"
                type="button"
                class="btn-icon-action"
                :title="s__('Attachments|View image')"
                @click="openImageGallery(attachment)"
              >
                <EyeIcon :size="16" />
              </button>
              <button
                type="button"
                class="btn-icon-action"
                :title="s__('Attachments|Download')"
                :disabled="downloading"
                @click="handleDownload(attachment)"
              >
                <DownloadIcon :size="16" />
              </button>
              <AttachmentSendToTelegramButton
                :disabled="sendingToTelegram"
                @send="sendToTelegram(attachment)"
              />
              <AttachmentPublicLinkButton
                :attachment="attachment"
                @updated="handlePublicLinkUpdated"
              />
              <button
                v-if="canDelete(attachment)"
                type="button"
                class="btn-icon-action btn-icon-delete"
                :title="s__('Attachments|Delete')"
                @click="confirmDelete(attachment)"
              >
                <TrashIcon :size="16" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="!canUpload" class="empty-state">
        {{ s__('Attachments|No attachments') }}
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <ConfirmModal
      :visible="showConfirmModal"
      :title="s__('Attachments|Delete Attachment?')"
      :message="
        s__(
          'Attachments|Are you sure you want to delete this attachment? This action cannot be undone.'
        )
      "
      :confirm-text="s__('Attachments|Delete')"
      :cancel-text="s__('Attachments|Cancel')"
      @confirm="deleteAttachment"
      @cancel="cancelDelete"
    />

    <!-- Image Gallery Modal -->
    <ImageGalleryModal
      :visible="showImageGallery"
      :images="galleryImages"
      :initial-index="galleryInitialIndex"
      @close="closeImageGallery"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useLazyQuery, useMutation } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { s__, __, n__ } from '~/locale'
import { formatRelativeDate } from '@/utils/dateFormat'
import FileIcon from '~/components/common/icons/FileIcon.vue'
import ImageFileIcon from '~/components/common/icons/ImageFileIcon.vue'
import VideoFileIcon from '~/components/common/icons/VideoFileIcon.vue'
import AudioFileIcon from '~/components/common/icons/AudioFileIcon.vue'
import DocumentFileIcon from '~/components/common/icons/DocumentFileIcon.vue'
import ArchiveFileIcon from '~/components/common/icons/ArchiveFileIcon.vue'
import TextFileIcon from '~/components/common/icons/TextFileIcon.vue'
import UploadIcon from '~/components/common/icons/UploadIcon.vue'
import DownloadIcon from '~/components/common/icons/DownloadIcon.vue'
import TrashIcon from '~/components/common/icons/TrashIcon.vue'
import CloseIcon from '~/components/common/icons/CloseIcon.vue'
import EyeIcon from '~/components/common/icons/EyeIcon.vue'
import WarningIcon from '~/components/common/icons/WarningIcon.vue'
import AttachmentSendToTelegramButton from '~/components/common/AttachmentSendToTelegramButton.vue'
import ConfirmModal from '~/components/common/ConfirmModal.vue'
import AlertBanner from '~/components/common/AlertBanner.vue'
import ImageGalleryModal from '~/components/common/ImageGalleryModal.vue'
import AttachmentsWebViewInfoModal from '~/components/common/AttachmentsWebViewInfoModal.vue'
import AttachmentsFileTypesModal from '~/components/common/AttachmentsFileTypesModal.vue'
import AttachmentPublicLinkButton from '~/components/common/AttachmentPublicLinkButton.vue'
import { useAttachmentDownload } from '@/composables/useAttachmentDownload'

const props = defineProps({
  attachableType: {
    type: String,
    required: true,
    validator: value => ['task', 'calendarEvent'].includes(value),
  },
  attachableId: {
    type: [String, Number],
    required: true,
  },
  projectId: {
    type: [String, Number],
    default: null,
  },
  currentUserId: {
    type: [String, Number],
    required: true,
  },
  authorId: {
    type: [String, Number],
    required: true,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
  expanded: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['uploaded', 'deleted', 'count-changed'])

const fileInput = ref(null)
const isDragging = ref(false)
const dragCounter = ref(0)
const uploadingFiles = ref([])
const uploadError = ref('')
const showErrorBanner = ref(false)
const showConfirmModal = ref(false)
const attachmentToDelete = ref(null)
const showImageGallery = ref(false)
const galleryImages = ref([])
const galleryInitialIndex = ref(0)
const showExistingAttachmentModal = ref(false)
const selectedExistingAttachmentIds = ref([])
const existingAttachmentSearch = ref('')
const attachExistingError = ref('')
const attachingExisting = ref(false)

// Download composable
const { downloading, downloadError, downloadAttachment, isTelegramWebView } =
  useAttachmentDownload()

// Send to Telegram state
const sendingToTelegram = ref(false)
const sendToTelegramError = ref(null)
const sendToTelegramInfo = ref(false)

// WebView info modal state
const showWebViewInfoModal = ref(false)

// File types info modal state
const showFileTypesModal = ref(false)

const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024 // 100 MB
const MAX_FILENAME_LENGTH = 255

// Define both queries
const GET_TASK_ATTACHMENTS_QUERY = gql`
  query GetTaskAttachments($taskId: ID!) {
    task(id: $taskId) {
      id
      author {
        id
      }
      project {
        id
        storageUsedMb
        storageLimitMb
        storagePercentageUsed
      }
      attachments {
        id
        filename
        byteSize
        contentType
        createdAt
        downloadUrl
        humanSize
        canDelete
        canCreatePublicLink
        canManagePublicLink
        publicLink {
          id
          expiresAt
          passwordProtected
          publicUrl
          revoked
          expired
        }
      }
    }
  }
`

const GET_CALENDAR_EVENT_ATTACHMENTS_QUERY = gql`
  query GetCalendarEventAttachments($eventId: ID!) {
    calendarEvent(id: $eventId) {
      id
      author {
        id
      }
      project {
        id
        storageUsedMb
        storageLimitMb
        storagePercentageUsed
      }
      attachments {
        id
        filename
        byteSize
        contentType
        createdAt
        downloadUrl
        humanSize
        canDelete
        canCreatePublicLink
        canManagePublicLink
        publicLink {
          id
          expiresAt
          passwordProtected
          publicUrl
          revoked
          expired
        }
      }
    }
  }
`

const GET_PROJECT_ATTACHMENTS_QUERY = gql`
  query GetProjectAttachmentsForTaskPicker($projectId: ID!, $first: Int) {
    project(id: $projectId) {
      id
      attachments(first: $first, projectOnly: true) {
        edges {
          node {
            id
            filename
            byteSize
            contentType
            createdAt
            downloadUrl
            humanSize
            canDelete
            canCreatePublicLink
            canManagePublicLink
            publicLink {
              id
              expiresAt
              passwordProtected
              publicUrl
              revoked
              expired
            }
          }
        }
      }
    }
  }
`

const DESTROY_ATTACHMENT_MUTATION = gql`
  mutation DestroyAttachment($id: ID!, $taskId: ID) {
    destroyAttachment(id: $id, taskId: $taskId) {
      success
      errors
    }
  }
`

const ATTACH_EXISTING_TASK_ATTACHMENTS_MUTATION = gql`
  mutation AttachExistingTaskAttachments($taskId: ID!, $attachmentIds: [ID!]!) {
    attachExistingTaskAttachments(taskId: $taskId, attachmentIds: $attachmentIds) {
      attachments {
        id
        filename
        byteSize
        contentType
        createdAt
        downloadUrl
        humanSize
        canDelete
        canCreatePublicLink
        canManagePublicLink
        publicLink {
          id
          expiresAt
          passwordProtected
          publicUrl
          revoked
          expired
        }
      }
      errors
    }
  }
`

const SEND_TO_TELEGRAM_MUTATION = gql`
  mutation SendAttachmentToTelegram($id: ID!) {
    sendAttachmentToTelegram(id: $id) {
      success
      errors
    }
  }
`

// Select the appropriate query based on attachable type
const selectedQuery =
  props.attachableType === 'task'
    ? GET_TASK_ATTACHMENTS_QUERY
    : GET_CALENDAR_EVENT_ATTACHMENTS_QUERY

// Build query variables based on attachable type
const queryVariables = computed(() => {
  if (props.attachableType === 'task') {
    return { taskId: props.attachableId }
  } else {
    return { eventId: props.attachableId }
  }
})

const { load, result, loading, refetch } = useLazyQuery(selectedQuery, queryVariables, {
  fetchPolicy: 'network-only',
})

const projectId = computed(() => {
  if (props.attachableType === 'task') {
    return result.value?.task?.project?.id || null
  }
  return result.value?.calendarEvent?.project?.id || null
})

const {
  load: loadProjectAttachments,
  result: projectAttachmentsResult,
  loading: projectAttachmentsLoading,
  refetch: refetchProjectAttachments,
} = useLazyQuery(GET_PROJECT_ATTACHMENTS_QUERY, () => ({ projectId: projectId.value, first: 50 }), {
  fetchPolicy: 'network-only',
})

const { mutate: deleteMutation } = useMutation(DESTROY_ATTACHMENT_MUTATION)
const { mutate: sendToTelegramMutation } = useMutation(SEND_TO_TELEGRAM_MUTATION)
const { mutate: attachExistingMutation } = useMutation(ATTACH_EXISTING_TASK_ATTACHMENTS_MUTATION)

// Get attachments from result based on attachable type
const attachments = computed(() => {
  if (props.attachableType === 'task') {
    return result.value?.task?.attachments || []
  } else {
    return result.value?.calendarEvent?.attachments || []
  }
})

// Last known count - preserved during loading to avoid flashing 0
const lastKnownCount = ref(null)

// Get storage info from result based on attachable type
const storageInfo = computed(() => {
  const project =
    props.attachableType === 'task'
      ? result.value?.task?.project
      : result.value?.calendarEvent?.project
  if (!project) return null
  return {
    usedMb: project.storageUsedMb,
    limitMb: project.storageLimitMb,
    percentageUsed: project.storagePercentageUsed.toFixed(1),
  }
})

const linkedAttachmentIds = computed(
  () => new Set(attachments.value.map(attachment => attachment.id))
)
const projectAttachments = computed(
  () => projectAttachmentsResult.value?.project?.attachments?.edges?.map(edge => edge.node) || []
)
const existingAttachableAttachments = computed(() =>
  projectAttachments.value.filter(attachment => !linkedAttachmentIds.value.has(attachment.id))
)
const filteredExistingAttachments = computed(() => {
  const search = existingAttachmentSearch.value.toLowerCase()
  if (!search) return existingAttachableAttachments.value

  return existingAttachableAttachments.value.filter(attachment =>
    attachment.filename.toLowerCase().includes(search)
  )
})

const canUpload = computed(() => {
  if (props.attachableType === 'task') {
    return true // Anyone who can read can upload
  } else {
    return !props.readOnly
  }
})

const canAttachExisting = computed(
  () => props.attachableType === 'task' && canUpload.value && !!projectId.value
)

const canDelete = attachment => {
  // Use the canDelete field from the backend (which checks the policy)
  // Also respect readOnly prop
  return !props.readOnly && attachment.canDelete
}

const handlePublicLinkUpdated = ({ attachmentId, publicLink }) => {
  const attachment = attachments.value.find(item => item.id === attachmentId)
  if (attachment) {
    attachment.publicLink = publicLink
  }
}

const openExistingAttachmentModal = async () => {
  attachExistingError.value = ''
  existingAttachmentSearch.value = ''
  selectedExistingAttachmentIds.value = []
  showExistingAttachmentModal.value = true

  if (!projectId.value) return

  const loaded = await loadProjectAttachments()
  if (loaded === false) {
    await refetchProjectAttachments({ projectId: projectId.value, first: 50 })
  }
}

const closeExistingAttachmentModal = () => {
  showExistingAttachmentModal.value = false
  selectedExistingAttachmentIds.value = []
  attachExistingError.value = ''
}

const attachSelectedExistingAttachments = async () => {
  if (selectedExistingAttachmentIds.value.length === 0) return

  attachingExisting.value = true
  attachExistingError.value = ''
  try {
    const { data } = await attachExistingMutation({
      taskId: props.attachableId,
      attachmentIds: selectedExistingAttachmentIds.value,
    })
    const result = data?.attachExistingTaskAttachments
    if (result?.errors?.length) {
      attachExistingError.value = result.errors.join(', ')
      return
    }

    await refetch()
    emit('uploaded', result?.attachments || [])
    closeExistingAttachmentModal()
  } catch (error) {
    attachExistingError.value = error.message || s__('Attachments|Failed to attach existing files')
  } finally {
    attachingExisting.value = false
  }
}

// Watch for changes to trigger loading
watch(
  () => {
    if (props.attachableType === 'task') {
      return props.expanded
    } else {
      return props.attachableId
    }
  },
  value => {
    if (props.attachableType === 'task') {
      // For tasks, load when expanded
      if (value && !result.value) {
        load()
      }
    } else {
      // For calendar events, load when eventId changes
      if (value) {
        load()
      }
    }
  },
  { immediate: true }
)

// Update lastKnownCount only when we have actual data (result loaded)
watch(
  () => result.value,
  value => {
    if (value) {
      lastKnownCount.value = attachments.value.length
      emit('count-changed', attachments.value.length)
    }
  },
  { immediate: true }
)

const openFilePicker = () => {
  fileInput.value?.click()
}

const handleFileSelect = event => {
  const files = Array.from(event.target.files || [])
  processFiles(files)
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

const handleDragEnter = event => {
  event.preventDefault()
  event.stopPropagation()
  dragCounter.value++
  if (
    event.dataTransfer &&
    event.dataTransfer.types &&
    event.dataTransfer.types.includes('Files')
  ) {
    isDragging.value = true
  }
}

const handleDragOver = event => {
  event.preventDefault()
  event.stopPropagation()
  if (
    event.dataTransfer &&
    event.dataTransfer.types &&
    event.dataTransfer.types.includes('Files')
  ) {
    isDragging.value = true
  }
}

const handleDragLeave = event => {
  event.preventDefault()
  event.stopPropagation()
  dragCounter.value--
  if (dragCounter.value <= 0) {
    dragCounter.value = 0
    isDragging.value = false
  }
}

const handleDrop = event => {
  event.preventDefault()
  event.stopPropagation()
  dragCounter.value = 0
  isDragging.value = false

  const files = Array.from(event.dataTransfer?.files || [])
  if (files.length > 0) {
    processFiles(files)
  }
}

const processFiles = files => {
  uploadError.value = ''
  showErrorBanner.value = false

  const validFiles = []
  for (const file of files) {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      uploadError.value = s__('Attachments|One or more files exceed the 100 MB limit')
      showErrorBanner.value = true
      continue
    }
    if (file.name.length > MAX_FILENAME_LENGTH) {
      uploadError.value = s__('Attachments|Filename too long (max 255 characters)')
      showErrorBanner.value = true
      continue
    }
    validFiles.push(file)
  }

  if (validFiles.length > 0) {
    uploadFiles(validFiles)
  }
}

const uploadFiles = async files => {
  uploadError.value = ''
  showErrorBanner.value = false

  const fileUploads = files.map(file => ({
    id: Date.now() + Math.random(),
    name: file.name,
    size: file.size,
    file: file,
    status: 'uploading',
    progress: 0,
    error: null,
    jobId: null,
  }))

  uploadingFiles.value.push(...fileUploads)

  // Upload all files in parallel
  const uploadPromises = fileUploads.map(async upload => {
    try {
      const formData = new FormData()
      formData.append('files[]', upload.file)

      // Add the appropriate parameter based on attachable type
      if (props.attachableType === 'task') {
        formData.append('task_id', props.attachableId)
      } else {
        formData.append('calendar_event_id', props.attachableId)
      }

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ''

      const response = await fetch('/attachments', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
        },
        body: formData,
        credentials: 'same-origin',
      })

      const data = await response.json()

      if (!response.ok || (data.errors && data.errors.length > 0)) {
        const rawErrors = data.errors || []
        const translatedErrors = rawErrors.map(translateError)
        const errorMessage = translatedErrors.join(', ') || s__('Attachments|Upload failed')
        upload.status = 'error'
        upload.progress = 0
        upload.error = errorMessage

        uploadError.value = errorMessage
        showErrorBanner.value = true
        return
      }

      // Check if we got a job_id (background job) or attachments (immediate success)
      if (data.job_id) {
        // Background job - start polling
        upload.jobId = data.job_id
        upload.status = 'processing'
        upload.progress = 0
        // Animate to initial progress
        animateProgress(upload, 10, 300)
        await pollJobStatus(upload)
      } else if (data.attachments) {
        // Immediate success (shouldn't happen with new backend, but handle gracefully)
        upload.progress = 100
        upload.status = 'success'
        await refetch()
        emit('uploaded', data.attachments)

        // Remove successful upload after refetch completes
        setTimeout(() => {
          const index = uploadingFiles.value.findIndex(f => f.id === upload.id)
          if (index !== -1) {
            uploadingFiles.value.splice(index, 1)
          }
        }, 1000)
      }
    } catch (error) {
      console.error('Upload error:', error)
      const errorMessage = s__('Attachments|Upload failed. Please try again.')
      upload.status = 'error'
      upload.error = errorMessage
      uploadError.value = errorMessage
      showErrorBanner.value = true
    }
  })

  await Promise.all(uploadPromises)
}

const animateProgress = (upload, targetProgress, duration = 500) => {
  const startProgress = upload.progress
  const startTime = Date.now()

  const animate = () => {
    // Stop animation if status is error
    if (upload.status === 'error') {
      return
    }

    const elapsed = Date.now() - startTime
    const progress = Math.min(elapsed / duration, 1)

    // Use easing function for smooth animation (ease-out)
    const easeOut = 1 - Math.pow(1 - progress, 3)
    upload.progress = Math.round(startProgress + (targetProgress - startProgress) * easeOut)

    if (progress < 1) {
      requestAnimationFrame(animate)
    } else {
      upload.progress = targetProgress
    }
  }

  requestAnimationFrame(animate)
}

const pollJobStatus = async upload => {
  if (!upload.jobId) return

  const uploadId = upload.id
  const jobId = upload.jobId
  const maxAttempts = 120 // Poll for up to 2 minutes (120 * 1s)
  let attempts = 0
  const pollInterval = 1000 // Poll every 1 second

  const poll = async () => {
    // Find the current upload object from the reactive array to ensure we have the latest reference
    const currentUpload = uploadingFiles.value.find(f => f.id === uploadId)
    if (!currentUpload) {
      // Upload was removed, stop polling
      return
    }

    if (attempts >= maxAttempts) {
      currentUpload.status = 'error'
      currentUpload.progress = 0
      currentUpload.error = s__('Attachments|Upload timed out. Please try again.')
      uploadError.value = currentUpload.error
      showErrorBanner.value = true
      return
    }

    try {
      const response = await fetch(`/attachments/job_status?job_id=${jobId}`, {
        method: 'GET',
        credentials: 'same-origin',
      })

      const data = await response.json()

      if (data.status === 'completed') {
        // Upload completed successfully - animate to 100%
        animateProgress(currentUpload, 100, 300)

        // Wait for animation to complete, then show success status
        setTimeout(() => {
          currentUpload.status = 'success'

          // Refresh attachments list
          refetch().then(() => {
            emit('uploaded', [])
          })

          // Remove successful upload after showing 100% for 1 second
          setTimeout(() => {
            const index = uploadingFiles.value.findIndex(f => f.id === uploadId)
            if (index !== -1) {
              uploadingFiles.value.splice(index, 1)
            }
          }, 1000)
        }, 300) // Wait for animation to complete
        return
      } else if (data.status === 'failed') {
        // Upload failed - stop any ongoing animations and set error state immediately
        currentUpload.status = 'error'
        currentUpload.progress = 0
        currentUpload.error = data.error || s__('Attachments|Upload failed. Please try again.')
        uploadError.value = currentUpload.error
        showErrorBanner.value = true
        return
      } else if (data.status === 'retrying') {
        // Upload failed but will retry
        currentUpload.status = 'processing'
        const targetProgress = Math.min(90, currentUpload.progress + 5)
        animateProgress(currentUpload, targetProgress, 500)
        attempts++
        setTimeout(poll, pollInterval)
      } else {
        // Still processing (enqueued, processing, scheduled)
        currentUpload.status = 'processing'
        // Gradually increase progress while processing
        const targetProgress = Math.min(90, 10 + attempts * 2)
        animateProgress(currentUpload, targetProgress, 500)
        attempts++
        setTimeout(poll, pollInterval)
      }
    } catch (error) {
      console.error('Error polling job status:', error)
      // Find the current upload object from the reactive array
      const currentUpload = uploadingFiles.value.find(f => f.id === uploadId)
      if (!currentUpload) {
        // Upload was removed, stop polling
        return
      }
      // Continue polling on error (might be transient)
      attempts++
      if (attempts < maxAttempts) {
        setTimeout(poll, pollInterval)
      } else {
        currentUpload.status = 'error'
        currentUpload.progress = 0
        currentUpload.error = s__(
          'Attachments|Failed to check upload status. Please refresh the page.'
        )
        uploadError.value = currentUpload.error
        showErrorBanner.value = true
      }
    }
  }

  // Start polling
  setTimeout(poll, pollInterval)
}

const cancelUpload = uploadId => {
  const index = uploadingFiles.value.findIndex(f => f.id === uploadId)
  if (index !== -1) {
    uploadingFiles.value.splice(index, 1)
  }
}

const retryUpload = uploadId => {
  const index = uploadingFiles.value.findIndex(f => f.id === uploadId)
  if (index !== -1) {
    const upload = uploadingFiles.value[index]
    uploadingFiles.value.splice(index, 1)
    uploadError.value = ''
    showErrorBanner.value = false
    uploadFiles([upload.file])
  }
}

const dismissFailedUpload = uploadId => {
  const index = uploadingFiles.value.findIndex(f => f.id === uploadId)
  if (index !== -1) {
    uploadingFiles.value.splice(index, 1)
  }
}

const isImageAttachment = attachment => {
  if (!attachment.contentType) {
    // Fallback to filename extension
    const ext = attachment.filename.split('.').pop()?.toLowerCase()
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)
  }
  return attachment.contentType.startsWith('image/')
}

const openImageGallery = attachment => {
  // Filter to only image attachments
  const imageAttachments = attachments.value.filter(isImageAttachment)
  const imageIndex = imageAttachments.findIndex(img => img.id === attachment.id)

  if (imageIndex !== -1) {
    galleryImages.value = imageAttachments
    galleryInitialIndex.value = imageIndex
    showImageGallery.value = true
  }
}

const closeImageGallery = () => {
  showImageGallery.value = false
  galleryImages.value = []
  galleryInitialIndex.value = 0
}

const handleDownload = async attachment => {
  await downloadAttachment(attachment.downloadUrl, attachment.filename)
}

const confirmDelete = attachment => {
  attachmentToDelete.value = attachment
  showConfirmModal.value = true
}

const cancelDelete = () => {
  showConfirmModal.value = false
  attachmentToDelete.value = null
}

const deleteAttachment = async () => {
  if (!attachmentToDelete.value) return

  try {
    const variables = { id: attachmentToDelete.value.id }
    if (props.attachableType === 'task') {
      variables.taskId = props.attachableId
    }
    const { data } = await deleteMutation(variables)

    if (data?.destroyAttachment?.errors?.length) {
      const errorMsg = data.destroyAttachment.errors.join(', ')
      uploadError.value = s__('Attachments|Failed to delete: %{error}').replace(
        '%{error}',
        errorMsg
      )
    } else if (data?.destroyAttachment?.success) {
      await refetch()
      emit('deleted', attachmentToDelete.value.id)
    }
  } catch (error) {
    console.error('Delete error:', error)
    uploadError.value = s__('Attachments|Failed to delete attachment')
  } finally {
    cancelDelete()
  }
}

const sendToTelegram = async attachment => {
  if (!attachment) return

  sendingToTelegram.value = true
  sendToTelegramError.value = null
  sendToTelegramInfo.value = false

  try {
    const { data } = await sendToTelegramMutation({ id: attachment.id })

    if (data?.sendAttachmentToTelegram?.errors?.length) {
      // Error occurred (rate limit, missing file, authorization, etc.) - show error immediately
      const errorMsg = data.sendAttachmentToTelegram.errors.join(', ')
      sendToTelegramError.value = s__('Attachments|Failed to send to Telegram: %{error}').replace(
        '%{error}',
        errorMsg
      )
      sendToTelegramInfo.value = false
    } else if (data?.sendAttachmentToTelegram?.success) {
      // Job was successfully enqueued - show info banner
      sendToTelegramInfo.value = true
      // Hide info banner after a short delay
      setTimeout(() => {
        sendToTelegramInfo.value = false
      }, 5000)
    }
  } catch (error) {
    console.error('Send to Telegram error:', error)
    sendToTelegramError.value = s__('Attachments|Failed to send to Telegram')
    sendToTelegramInfo.value = false
  } finally {
    sendingToTelegram.value = false
  }
}

const getFileIconClass = filename => {
  const ext = filename.split('.').pop()?.toLowerCase()

  if (['pdf'].includes(ext)) return 'file-icon-pdf'
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'file-icon-image'
  if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) return 'file-icon-video'
  if (['mp3', 'ogg', 'wav', 'm4a', 'flac'].includes(ext)) return 'file-icon-audio'
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return 'file-icon-archive'
  if (['doc', 'docx', 'txt'].includes(ext)) return 'file-icon-document'

  return 'file-icon-default'
}

const getFileIcon = (contentType, filename) => {
  // First try to determine by content type
  if (contentType) {
    if (contentType.startsWith('image/')) return ImageFileIcon
    if (contentType.startsWith('video/')) return VideoFileIcon
    if (contentType.startsWith('audio/')) return AudioFileIcon
    if (contentType.includes('pdf')) return DocumentFileIcon
    if (contentType.includes('epub')) return DocumentFileIcon
    if (contentType.includes('word') || contentType.includes('document')) return DocumentFileIcon
    if (contentType.includes('excel') || contentType.includes('spreadsheet'))
      return DocumentFileIcon
    if (contentType.includes('powerpoint') || contentType.includes('presentation'))
      return DocumentFileIcon
    if (
      contentType.includes('zip') ||
      contentType.includes('rar') ||
      contentType.includes('7z') ||
      contentType.includes('tar') ||
      contentType.includes('gzip')
    )
      return ArchiveFileIcon
    if (contentType.startsWith('text/')) return TextFileIcon
  }

  // Fallback to filename extension if content type is unknown or doesn't match
  if (filename) {
    const ext = filename.split('.').pop()?.toLowerCase()
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return ImageFileIcon
    if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) return VideoFileIcon
    if (['mp3', 'ogg', 'wav', 'm4a', 'flac'].includes(ext)) return AudioFileIcon
    if (['pdf', 'epub'].includes(ext)) return DocumentFileIcon
    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) return DocumentFileIcon
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return ArchiveFileIcon
    if (['txt', 'csv', 'md'].includes(ext)) return TextFileIcon
  }

  // Default to FileIcon for unknown types
  return FileIcon
}

const getIconForProgress = file => {
  // Show success icon only for successful uploads
  if (file.status === 'success') return UploadIcon

  // Determine icon based on file type first (even for errors)
  const ext = file.name.split('.').pop()?.toLowerCase()
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return ImageFileIcon
  if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) return VideoFileIcon
  if (['mp3', 'ogg', 'wav', 'm4a', 'flac'].includes(ext)) return AudioFileIcon
  if (['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) return DocumentFileIcon
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return ArchiveFileIcon
  if (['txt', 'csv', 'md'].includes(ext)) return TextFileIcon

  // For unknown file types, show FileIcon (with question mark)
  // Only show CloseIcon if we have no file name to determine type
  if (!file.name || file.name.trim() === '') {
    return file.status === 'error' ? CloseIcon : FileIcon
  }

  return FileIcon
}

const formatDate = formatRelativeDate

// Translate backend error messages
const translateError = errorMessage => {
  if (!errorMessage) return errorMessage

  // File type not allowed: filename (content_type)
  if (errorMessage.match(/^File type not allowed:/)) {
    const match = errorMessage.match(/^File type not allowed: (.+?) \((.+?)\)$/)
    if (match) {
      const filename = match[1]
      const contentType = match[2]
      return s__('Attachments|File type not allowed: %{filename} (%{contentType})')
        .replace('%{filename}', filename)
        .replace('%{contentType}', contentType)
    }
    return s__('Attachments|File type not allowed')
  }

  // File too large: filename (max X MB)
  if (errorMessage.match(/^File too large:/)) {
    const match = errorMessage.match(/^File too large: (.+?) \(max (.+?) MB\)$/)
    if (match) {
      const filename = match[1]
      const maxMb = match[2]
      return s__('Attachments|File too large: %{filename} (max %{maxMb} MB)')
        .replace('%{filename}', filename)
        .replace('%{maxMb}', maxMb)
    }
    return s__('Attachments|File too large')
  }

  // Filename too long: filename... (max X characters)
  if (errorMessage.match(/^Filename too long:/)) {
    const match = errorMessage.match(/^Filename too long: (.+?) \(max (\d+) characters\)$/)
    if (match) {
      const filename = match[1]
      const maxLength = match[2]
      return s__('Attachments|Filename too long: %{filename} (max %{maxLength} characters)')
        .replace('%{filename}', filename)
        .replace('%{maxLength}', maxLength)
    }
    return s__('Attachments|Filename too long')
  }

  // Insufficient storage: X MB required, Y MB available
  if (errorMessage.match(/^Insufficient storage:/)) {
    const match = errorMessage.match(
      /^Insufficient storage: (.+?) MB required, (.+?) MB available$/
    )
    if (match) {
      const requiredMb = match[1]
      const availableMb = match[2]
      return s__(
        'Attachments|Insufficient storage: %{requiredMb} MB required, %{availableMb} MB available'
      )
        .replace('%{requiredMb}', requiredMb)
        .replace('%{availableMb}', availableMb)
    }
    return s__('Attachments|Insufficient storage')
  }

  // No files provided
  if (errorMessage === 'No files provided') {
    return s__('Attachments|No files provided')
  }

  // Failed to upload attachments: message
  if (errorMessage.match(/^Failed to upload attachments:/)) {
    const match = errorMessage.match(/^Failed to upload attachments: (.+)$/)
    if (match) {
      const reason = match[1]
      return s__('Attachments|Failed to upload attachments: %{reason}').replace('%{reason}', reason)
    }
    return s__('Attachments|Failed to upload attachments')
  }

  // Failed to upload to storage: message
  if (errorMessage.match(/^Failed to upload to storage:/)) {
    const match = errorMessage.match(/^Failed to upload to storage: (.+)$/)
    if (match) {
      const reason = match[1]
      return s__('Attachments|Failed to upload to storage: %{reason}').replace('%{reason}', reason)
    }
    return s__('Attachments|Failed to upload to storage')
  }

  // Failed to start upload: message
  if (errorMessage.match(/^Failed to start upload:/)) {
    const match = errorMessage.match(/^Failed to start upload: (.+)$/)
    if (match) {
      const reason = match[1]
      return s__('Attachments|Failed to start upload: %{reason}').replace('%{reason}', reason)
    }
    return s__('Attachments|Failed to start upload')
  }

  // Return original message if no pattern matches
  return errorMessage
}
</script>

<style src="../../styles/attachments_list.css"></style>
<style scoped>
.attachments-container {
  padding: 24px;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.webview-warning-btn-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 6px;
  color: #92400e;
  cursor: pointer;
  transition: all 0.2s;
}

.webview-warning-btn-header:hover {
  background: #fde68a;
  border-color: #f59e0b;
}

.webview-warning-btn-header:active {
  background: #fcd34d;
}

.webview-warning-btn-header svg {
  color: #d97706;
}

.loading-state {
  text-align: center;
  padding: 40px;
  color: #6b7280;
}

.attachments-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
}

/* Upload Zone */
.upload-tools {
  display: grid;
  gap: 10px;
}

.upload-zone {
  padding: 16px 20px;
  border-radius: 12px;
  border: 2px dashed #d1d5db;
  background: #f9fafb;
  cursor: pointer;
  transition: all 0.2s;
}

.upload-zone:hover {
  border-color: rgba(42, 171, 238, 0.5);
  background: rgba(42, 171, 238, 0.02);
}

.upload-zone.drag-over {
  border-color: #2aabee;
  background: rgba(42, 171, 238, 0.05);
}

.upload-zone-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.upload-icon {
  width: 20px;
  height: 20px;
  color: #2aabee;
}

.upload-text {
  text-align: center;
}

.upload-title {
  font-size: 14px;
  font-weight: 500;
  margin-top: 2px;
  margin-bottom: 2px;
  color: #111827;
  display: flex;
  justify-content: center;
  column-gap: 12px;
}

.upload-subtitle {
  font-size: 12px;
  color: #6b7280;
}

.upload-subtitle-link {
  margin-left: 0.5rem;
  color: #2aabee;
  text-decoration: underline;
  background: transparent !important;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: inherit;
  font-family: inherit;
}

.upload-subtitle-link:hover {
  color: #1e8bc3;
  text-decoration: none;
  background: transparent !important;
}

.attach-existing-btn {
  align-self: flex-start;
  border: 1px solid #2aabee;
  border-radius: 8px;
  background: #fff;
  color: #2aabee;
  padding: 8px 12px;
  font-weight: 600;
  cursor: pointer;
}

.attach-existing-btn:hover {
  background: #f0f9ff;
}

.attach-existing-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.45);
}

.attach-existing-modal {
  width: min(100%, 560px);
  max-height: min(720px, 90vh);
  display: grid;
  gap: 14px;
  padding: 20px;
  border-radius: 12px;
  background: #fff;
  overflow: auto;
}

.attach-existing-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.attach-existing-modal-header h3 {
  margin: 0;
  color: #333;
  font-size: 18px;
}

.attach-existing-search {
  min-height: 40px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
}

.attach-existing-list {
  display: grid;
  gap: 8px;
}

.attach-existing-item {
  display: grid;
  grid-template-columns: auto auto minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  padding: 10px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
}

.attach-existing-item:hover {
  border-color: #2aabee;
  background: #f0f9ff;
}

.attach-existing-error {
  margin: 0;
  color: #dc2626;
  font-size: 13px;
}

.attach-existing-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

/* Upload Progress */
.upload-progress-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.upload-progress-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-radius: 12px;
  background: #f9fafb;
}

.file-progress-info {
  flex: 1;
  min-width: 0;
}

.file-progress-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.file-progress-name {
  font-size: 14px;
  color: #111827;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-progress-percent {
  font-size: 12px;
  color: #6b7280;
}

.progress-bar {
  height: 6px;
  border-radius: 3px;
  background: #e5e7eb;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: #2aabee;
  transition: width 0.2s ease-out;
}

.file-progress-success {
  font-size: 12px;
  color: #059669;
}

.file-progress-error {
  font-size: 12px;
  color: #dc2626;
}

.upload-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.btn-icon-cancel,
.btn-icon-dismiss,
.btn-retry {
  flex-shrink: 0;
  padding: 8px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-icon-cancel,
.btn-icon-dismiss {
  background: transparent;
  color: #6b7280;
}

.btn-icon-cancel:hover,
.btn-icon-dismiss:hover {
  background: #fee2e2;
  color: #dc2626;
}

.btn-retry {
  padding: 8px 16px;
  background: #2aabee;
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
}

.btn-retry:hover {
  background: #229ed9;
}

/* Storage Info Section */
.storage-info-section {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
}

/* Attachments List */
.attachments-list-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.storage-info-badge {
  font-size: 13px;
  color: #6b7280;
}
</style>
